import React, { useState } from 'react';
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import { createVerificationCode, checkVerificationCode, resetPortalPassword } from '../../api'
import { useSpring, animated } from 'react-spring'
import Input from '../../components/input.js'
import Button from '../../components/button.js'
import { withRouter } from 'react-router-dom'
import { ErrorMessage } from '../../components/error-message';

const Page = styled.div`
  background: #eff0f1;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
`;

const Container = styled(animated.div)`
  display: flex;

  @media(max-width: 1020px) {
    margin: 0px 16px;
    width: 100%;
  }
`;

const Requirements = styled.ul`
  margin: 0 0 32px 0;
  padding-left: 24px;
  li {
    color: #777;
    font-size: .925rem;
    margin: 4px 0px;
  }
`;

const Form = styled.div`
  background: #FFF;
  box-shadow: 0px 0px 15px 0px #cccfd1;
  border-radius: 8px;
  padding: 48px;
  width: 500px;

  @media(max-width: 1020px) {
    padding: 32px 24px;
    width: 100%;
  }
`;

const Title = styled.h2`
  color: #555;
  font-family: ${props => props.theme.font.primary};
  font-size: 1.25rem;
  font-weight: 500;
  margin: 0 0 32px 0;
`;

const Instructions = styled.p`
  color: #777;
  margin: 0 0 32px 0;
  span {
    font-weight: 600;
  }
`;

const HomeLink = styled(Link)`
  color: #888;
  display: block;
  font-family: ${props => props.theme.font.primary};
  font-size: .9375rem;
  margin-top: 24px;
  text-align: center;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const LoginButton = styled(Button)`
  width: 100%;
`;

const Reset = (props) => {
  const formAnimation = useSpring({ opacity: 1, from: { opacity: 0 } })
  const [form, updateForm] = useState('email')
  const [error, updateError] = useState(false)
  const [errorMessage, updateErrorMessage] = useState('')
  const [loading, updateLoading] = useState(false)
  const [email, updateEmail] = useState('')
  const [verifyCode, updateVerifyCode] = useState('')
  const [password, updatePassword] = useState('')
  const [confirmPassword, updateConfirmPassword] = useState('')

  const emailIsValid = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  const submitEmail = () => {
    if (emailIsValid(email)) {
      createVerificationCode(email)
        .then(res => {
          updateError(false)
          updateForm('code')
        })
        .catch(err => {
          updateError(false)
          updateForm('code')
        })
    } else {
      updateError(true)
      updateErrorMessage('Please enter a valid email address.')
    }
  }

  const submitCode = () => {
    updateLoading(true)

    checkVerificationCode(email, verifyCode)
      .then(res => {
        if (res.data.result === "Success") {
          updateLoading(false)
          updateForm('password');
          updateError(false)
          updateErrorMessage('')
        } else if (res.data.result === "Invalid") {
          updateLoading(false)
          updateError(true)
          updateErrorMessage('The code you entered is incorrect.')
        } else if (res.data.result === "Expired") { 
          updateLoading(false)
          updateError(true)
          updateErrorMessage('The code you entered has expired. Please send a new code to your email address.')
        }
      })
      .catch(err => console.log(err))
  }

  const handleEnterEmail = (e) => {
    if (e.key === 'Enter' && !loading) {
      submitEmail()
    }
  }

  const handleEnterCode = (e) => {
    if (e.key === 'Enter' && !loading) {
      submitCode()
    }
  }

  const handleEnterPassword = (e) => {
    if (e.key === 'Enter' && !loading) {
      submitPassword()
    }
  }

  const validatePassword = () => {
    if(password !== confirmPassword) {
      updateErrorMessage('Passwords do not match.')
      updateError(true)
      return false;
    } else if(password.length < 8) {
      updateErrorMessage('Password must be at least 8 characters.')
      updateError(true)
      return false;
    } else if(!(/(?=.*[a-z]+)/.test(password))) {
      updateErrorMessage('Password must have at least one uppercase and one lowercase letter.')
      updateError(true)
      return false;
    } else if(!(/(?=.*[A-Z]+)/.test(password))) {
      updateErrorMessage('Password must have at least one uppercase and one lowercase letter.')
      updateError(true)
      return false;
    } else if(!(/(?=.*\d+)/.test(password))) {
      updateErrorMessage('Password must have at least one number.')
      updateError(true)
      return false;
    }

    return true;
  }

  const submitPassword = () => {
    if (validatePassword()) {
      updateLoading(true)
      resetPortalPassword(email, verifyCode, password).then((res) => {
        updateLoading(false)
        props.history.push('/')
      }).catch((err) => {
        updateLoading(false)
        console.log(err)
      })
    }
  }

  const renderForm = (form) => {
    if (form === 'email') {
      return (
        <Form>
          <Title>Forgot Password</Title>
          <Instructions>Enter your email below and we'll send you a verification code to reset your password.</Instructions>
          <Input
            error={Boolean(error)}
            label="Email"
            type="email" 
            autoComplete="email" 
            value={email}
            onChange={updateEmail}
            onKeyPress={handleEnterEmail}
          />
          {error && 
            <ErrorMessage>
              {errorMessage}
            </ErrorMessage>
          }
          <LoginButton 
            label="Submit"
            isLoading={loading}
            disabled={loading}
            onClick={submitEmail}
          />
          <HomeLink to="/">Back to Login</HomeLink>
        </Form>
      )
    } else if (form === 'code') {
      return (
        <Form>
          <Title>Check Your Email</Title>
          <Instructions>If the email address is valid, a verification code will be sent to you. Please enter the code below to reset your password.</Instructions>
          <Input
            error={Boolean(error)}
            label="Verification Code"
            type="text" 
            value={verifyCode}
            onChange={updateVerifyCode}
            onKeyPress={handleEnterCode}
          />
          {error && 
            <ErrorMessage>
              {errorMessage}
            </ErrorMessage>
          }
          <LoginButton 
            label="Submit"
            isLoading={loading}
            disabled={loading}
            onClick={submitCode}
          />
        </Form>
      )
    } else if (form === 'password') {
      return (
        <Form>
          <Title>Set Your Password</Title>
          <Requirements>
            <li>Must be at least 8 characters.</li>
            <li>Must have at least one uppercase and one lowercase letter.</li>
            <li>Must have at least one number.</li>
          </Requirements>
          <Input
            error={Boolean(error)}
            label="Password"
            type="password" 
            autoComplete="current-password" 
            value={password}
            onChange={updatePassword}
          />
          <Input
            error={Boolean(error)}
            label="Re-enter Password"
            type="password" 
            autoComplete="current-password" 
            value={confirmPassword}
            onChange={updateConfirmPassword}
            onKeyPress={handleEnterPassword}
          />
          {error && 
            <ErrorMessage>
              {errorMessage}
            </ErrorMessage>
          }
          <LoginButton 
            label="Submit"
            isLoading={loading}
            disabled={loading}
            onClick={submitPassword}
          />
        </Form>
      )
    }
  }
  
  return (
    <Page>
      <Container style={formAnimation}>
        {renderForm(form)}
      </Container>
    </Page>
  )
}


export default withRouter(Reset)
